import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Base_Url } from '../config';
import { useParams, useNavigate } from 'react-router-dom';

const EditNews = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        external_link: '',
        internal_link: '',
        image: null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [currentImage, setCurrentImage] = useState('');
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        const fetchNewsDetails = async () => {
            try {
                const response = await axios.get(`${Base_Url}/newsandannouncement/${id}`);
                const newsData = response.data;
                setFormData({
                    title: newsData.title,
                    description: newsData.description,
                    external_link: newsData.external_link || '',
                    internal_link: newsData.internal_link || '',
                    image: null
                });
                setCurrentImage(newsData.image_path);
            } catch (err) {
                setError('Failed to fetch news details');
                console.error('Error fetching news details:', err);
            }
        };

        if (id) {
            fetchNewsDetails();
        }
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));

            // Create preview URL for the new image
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setFormData(prev => ({
                ...prev,
                image: null
            }));
            setImagePreview(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('external_link', formData.external_link);
            formDataToSend.append('internal_link', formData.internal_link);
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            await axios.put(`${Base_Url}/newsandannouncement/${id}`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSuccessMessage('News updated successfully!');
            setTimeout(() => {
                navigate('/');
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update news');
            console.error('Error updating news:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container py-4">
            <h1 className="h2 mb-4">Edit News & Announcement</h1>

            <div className="card mb-4">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Title *</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className="form-control"
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Description *</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                className="form-control"
                                rows="4"
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">External Link</label>
                            <input
                                type="url"
                                name="external_link"
                                value={formData.external_link}
                                onChange={handleInputChange}
                                className="form-control"
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Internal Link</label>
                            <input
                                type="text"
                                name="internal_link"
                                value={formData.internal_link}
                                onChange={handleInputChange}
                                className="form-control"
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Current Image</label>
                            {currentImage && !imagePreview && (
                                <img
                                    src={`${Base_Url}/${currentImage}`}
                                    alt="Current"
                                    className="img-fluid mt-2 mb-2"
                                    style={{ maxWidth: '200px', display: 'block' }}
                                />
                            )}
                            
                            <label className="form-label mt-3">Update Image</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="form-control"
                            />
                            
                            {imagePreview && (
                                <div className="mt-3">
                                    <label className="form-label">New Image Preview</label>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="img-fluid mt-2"
                                        style={{ maxWidth: '200px', display: 'block' }}
                                    />
                                </div>
                            )}
                        </div>

                        {error && <div className="alert alert-danger">{error}</div>}
                        {successMessage && <div className="alert alert-success">{successMessage}</div>}

                        <div className="d-flex gap-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn btn-primary"
                            >
                                {loading ? 'Updating...' : 'Update News'}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/')}
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditNews;